import { DropdownEditor } from '@approvalmax/ui';
import { statics } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import CurrencyExchangeRateInput from 'pages/requestForm/components/CurrencyExchangeRateInput/CurrencyExchangeRateInput';
import { LineItemsTable } from 'pages/requestForm/components/lineItemsSection/LineItemsTable/LineItemsTable';
import { SectionContainer } from 'pages/requestForm/components/SectionContainer';
import { findLineAmountType, lineAmountTypeReferences } from 'pages/requestForm/data/qbooks/statics/lineAmountType';
import { memo } from 'react';

import CurrencyDisclaimer from '../CurrencyDisclaimer/CurrencyDisclaimer';
import { useTableDef } from './JournalEntryLines.hooks';
import { messages } from './JournalEntryLines.messages';
import {
    HeaderRight,
    LineAmountTypeLabel,
    LineAmountTypeWrapper,
    SectionHeader,
    StyledExchangeRateWithUpdate,
} from './JournalEntryLines.styles';
import { JournalEntryLinesProps } from './JournalEntryLines.types';

const JournalEntryLines = memo<JournalEntryLinesProps>((props) => {
    const {
        companyCurrency,
        requestCurrency,
        lines,
        payees,
        lineAmountType,
        taxCodeList,
        isAUOrg,
        isUSOrg,
        isClassFieldRequired,
        isDepartmentFieldRequired,
        exchangeRate,
        showErrors,
        showErrorsOnEmptyLine,
        requestId,
        companyId,
        journalDate,
        isManualExchangeRate,
        onAddLine,
        onCloneLine,
        onRemoveLine,
        onReorderLines,
        onLineFieldUpdate,
        onLineAmountTypeChange,
        onExchangeRateChange,
        onAutoExchangeRateChange,
        isExchangeRateInputAvailable,
    } = props;

    const hideTaxColumn = lineAmountType === domain.LineAmountType.NoTax || isUSOrg;

    const tableDef = useTableDef({
        payees,
        taxCodeList,
        showErrors,
        showErrorsOnEmptyLine,
        showTaxColumn: !hideTaxColumn,
        isClassFieldRequired,
        isDepartmentFieldRequired,
        onLineFieldUpdate,
    });

    return (
        <SectionContainer
            title={
                <SectionHeader>
                    <div>{messages.sectionHeader}</div>

                    <HeaderRight>
                        {isManualExchangeRate ? (
                            <CurrencyDisclaimer
                                companyCurrency={companyCurrency}
                                requestCurrency={requestCurrency}
                                exchangeRate={exchangeRate}
                            />
                        ) : (
                            <StyledExchangeRateWithUpdate
                                requestId={requestId}
                                companyId={companyId}
                                date={journalDate}
                                currency={requestCurrency}
                                companyCurrency={companyCurrency}
                                onChange={onAutoExchangeRateChange}
                            />
                        )}

                        {isExchangeRateInputAvailable && (
                            <CurrencyExchangeRateInput
                                selectedCurrency={statics.currency.findCurrencyByCode(requestCurrency) || null}
                                defaultCurrency={statics.currency.findCurrencyByCode(companyCurrency) || null}
                                exchangeRateValue={exchangeRate}
                                onExchangeRateChange={onExchangeRateChange}
                            />
                        )}

                        {isAUOrg && (
                            <LineAmountTypeWrapper>
                                <LineAmountTypeLabel>Amounts are</LineAmountTypeLabel>

                                <PlainDataProvider items={lineAmountTypeReferences}>
                                    <DropdownEditor
                                        value={findLineAmountType(lineAmountType)}
                                        onChange={onLineAmountTypeChange}
                                        buttonComponent={DropdownEditor.NoFilterButton}
                                    />
                                </PlainDataProvider>
                            </LineAmountTypeWrapper>
                        )}
                    </HeaderRight>
                </SectionHeader>
            }
            noPadding
        >
            <LineItemsTable
                columnDefinitions={tableDef}
                lineItems={lines}
                invalid={false}
                onReorder={onReorderLines}
                onRemoveLine={onRemoveLine}
                onCloneLine={onCloneLine}
                onAddLine={onAddLine}
                canAddLine={true}
            />
        </SectionContainer>
    );
});

export default JournalEntryLines;
